// OMNI Universal Button Component using Chakra UI v3
// Provides consistent button styling based on OMNI design system

import React, { forwardRef } from 'react';
import { chakra } from '@chakra-ui/react';
import type { ButtonProps, IconButtonProps } from '@chakra-ui/react';

// =============================================================================
// OMNI Button Recipe Definition
// =============================================================================
const omniButtonRecipe = {
  className: 'omni-button',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    position: 'relative',
    borderRadius: 'var(--omni-radii-base)',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    cursor: 'pointer',
    flexShrink: 0,
    outline: 'transparent',
    isolation: 'isolate',
    fontWeight: 'var(--omni-font-weights-medium)',
    fontFamily: 'var(--omni-fonts-body)',
    transition: 'all 200ms ease',
    border: '1px solid transparent',
    textDecoration: 'none',
    _disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
      transform: 'none !important',
    },
    _focus: {
      boxShadow: '0 0 0 2px var(--omni-colors-brand-400)',
      outline: 'none',
    },
  },
  variants: {
    variant: {
      primary: {
        background: 'linear-gradient(135deg, #00a1d2 0%, #03bbe3 100%)',
        color: 'white',
        borderColor: '#00a1d2',
        boxShadow: '0 4px 6px 0 rgba(0, 161, 210, 0.25)',
        _hover: {
          background: 'linear-gradient(135deg, #0091be 0%, #03a8cc 100%)',
          boxShadow: '0 6px 8px -2px rgba(0, 161, 210, 0.3)',
          transform: 'translateY(-1px)',
        },
        _active: {
          transform: 'translateY(0)',
          boxShadow: '0 2px 4px 0 rgba(0, 161, 210, 0.2)',
        },
        _disabled: {
          background: '#e2e8f0',
          color: '#a0aec0',
          borderColor: '#e2e8f0',
          boxShadow: 'none',
        },
      },
      secondary: {
        background: 'white',
        color: '#00a1d2',
        borderColor: '#00a1d2',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        _hover: {
          background: '#f0f9ff',
          boxShadow: '0 4px 6px -1px rgba(0, 161, 210, 0.1)',
          transform: 'translateY(-1px)',
        },
        _active: {
          transform: 'translateY(0)',
          background: '#e6f7fd',
        },
        _disabled: {
          background: '#f7fafc',
          color: '#a0aec0',
          borderColor: '#e2e8f0',
        },
      },
      ghost: {
        background: 'transparent',
        color: '#1a202c',
        borderColor: 'transparent',
        _hover: {
          background: '#f7fafc',
          color: '#00a1d2',
        },
        _active: {
          background: '#edf2f7',
        },
        _disabled: {
          color: '#a0aec0',
          background: 'transparent',
        },
      },
      danger: {
        background: 'linear-gradient(137deg, #df5678 4%, #f56c8e)',
        color: 'white',
        borderColor: '#df5678',
        boxShadow: '0 4px 6px 0 rgba(223, 86, 120, 0.25)',
        _hover: {
          background: 'linear-gradient(137deg, #c8407a 4%, #e84469)',
          boxShadow: '0 6px 8px -2px rgba(223, 86, 120, 0.3)',
          transform: 'translateY(-1px)',
        },
        _active: {
          transform: 'translateY(0)',
        },
        _disabled: {
          background: '#fed7d7',
          color: '#a0aec0',
          borderColor: '#fed7d7',
          boxShadow: 'none',
        },
      },
    },
    size: {
      xs: {
        height: '1.5rem',
        minWidth: '1.5rem',
        fontSize: 'var(--omni-font-sizes-xs)',
        px: '0.5rem',
      },
      sm: {
        height: '2rem',
        minWidth: '2rem',
        fontSize: 'var(--omni-font-sizes-sm)',
        px: '0.75rem',
      },
      md: {
        height: '2.5rem',
        minWidth: '2.5rem',
        fontSize: 'var(--omni-font-sizes-md)',
        px: '1rem',
      },
      lg: {
        height: '3rem',
        minWidth: '3rem',
        fontSize: 'var(--omni-font-sizes-lg)',
        px: '1.25rem',
      },
      xl: {
        height: '3.5rem',
        minWidth: '3.5rem',
        fontSize: 'var(--omni-font-sizes-xl)',
        px: '1.5rem',
      },
    },
  },
  defaultVariants: {
    variant: 'primary' as const,
    size: 'md' as const,
  },
};

// Create styled components
const StyledButton = chakra('button', omniButtonRecipe);
const StyledIconButton = chakra('button', omniButtonRecipe);

// =============================================================================
// OMNI Button Component Types
// =============================================================================
export interface OmniButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
  loadingText?: string;
}

export interface OmniIconButtonProps
  extends Omit<IconButtonProps, 'variant' | 'size'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

// =============================================================================
// OMNI Button Component
// =============================================================================
export const OmniButton = forwardRef<HTMLButtonElement, OmniButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      isLoading,
      loadingText,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <StyledButton
        ref={ref}
        variant={variant}
        size={size}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <chakra.span
              display="inline-block"
              animation="spin 1s linear infinite"
              border="2px solid currentColor"
              borderTopColor="transparent"
              borderRadius="50%"
              width="1em"
              height="1em"
              mr={loadingText ? 2 : 0}
            />
            {loadingText}
          </>
        ) : (
          children
        )}
      </StyledButton>
    );
  },
);

// =============================================================================
// OMNI Icon Button Component
// =============================================================================
export const OmniIconButton = forwardRef<
  HTMLButtonElement,
  OmniIconButtonProps
>(({ children, variant = 'ghost', size = 'md', ...props }, ref) => {
  return (
    <StyledIconButton
      ref={ref}
      variant={variant}
      size={size}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      {...props}
    >
      {children}
    </StyledIconButton>
  );
});

// Set display names for debugging
OmniButton.displayName = 'OmniButton';
OmniIconButton.displayName = 'OmniIconButton';
